<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useOnboardingStore } from "@/stores/useOnboardingStore";
import { useUserStore } from "@/stores/useUserStore";
import * as managedAccountService from "@/services/managedAccountService";
import { SUGGESTED_MEMBER_PROFILES } from "@/utils/suggestedMemberProfiles";
import ManagedAccountOnboarding from "@/components/ManagedAccount/ManagedAccountOnboarding.vue";

const router = useRouter();
const onboardingStore = useOnboardingStore();
const userStore = useUserStore();

// Nome do plano em que a escola acabou de entrar ("Edu Free" no cadastro;
// o pago, quando veio da página de planos). Vem do login/refresh do usuário.
const planName = computed(() => (userStore.currentUser?.planName || "").trim());

// 'papeis' | 'aviso' | 'pessoas' | 'sucesso'
const step = ref("papeis");

const activeManagedAccounts = ref(0);
const maxManagedAccounts = ref(0);
// Quantas contas já existiam antes de abrir o cadastro de pessoas — usado
// pra calcular quantas foram criadas nesta passada pelo wizard.
const accountsBeforePessoas = ref(0);
const createdCount = ref(0);

async function loadUsage() {
  try {
    const usage = await managedAccountService.fetchAggregatedUsage();
    activeManagedAccounts.value = usage?.data?.activeManagedAccounts ?? 0;
    maxManagedAccounts.value = usage?.data?.maxManagedAccounts ?? 0;
  } catch (e) {
    console.error("[SchoolSetupWizard] Falha ao ler o consumo de contas:", e);
  }
}

// A consulta começa junto com o wizard: enquanto a escola lê a etapa de
// papéis, a resposta chega, e a tela de aviso já nasce com o número certo,
// em vez de piscar "Verificando seu plano…" antes do texto de verdade.
let usageReady = Promise.resolve();
onMounted(() => {
  usageReady = loadUsage();
});

async function goToAviso() {
  await usageReady;
  accountsBeforePessoas.value = activeManagedAccounts.value;
  step.value = "aviso";
}

function goToPessoas() {
  step.value = "pessoas";
}

// "Fazer depois": a escola pode cadastrar as pessoas a qualquer momento em
// /dashboard/member-accounts, então quem adia vai para a Estante, o ponto de
// partida normal da conta, e não para a tela de contas que acabou de recusar.
function skipForNow() {
  onboardingStore.reset();
  router.push({ name: "TextCardsForm" });
}

// ManagedAccountOnboarding emite done com { skipped: false } só quando o
// lote foi enviado com sucesso pelo botão "Concluir". Cancelar (botão
// "Cancelar" ou o "×") e fechar sem ter adicionado ninguém emitem outra
// coisa — nesses casos não há nada novo pra resumir, então volta direto
// pro painel em vez de repetir uma tela de "pronto".
async function handleOnboardingDone(payload) {
  if (payload?.skipped !== false) {
    finish();
    return;
  }
  await loadUsage();
  createdCount.value = Math.max(
    0,
    activeManagedAccounts.value - accountsBeforePessoas.value
  );
  step.value = "sucesso";
}

function finish() {
  onboardingStore.reset();
  router.push({ name: "MemberAccountForm" });
}
</script>

<template>
  <ManagedAccountOnboarding
    v-if="step === 'pessoas'"
    @done="handleOnboardingDone"
  />

  <div v-else class="modal-backdrop">
    <!-- Papéis: painel largo do wizard -->
    <div v-if="step === 'papeis'" class="modal-panel">
      <div class="form-column">
        <div class="modal-header">
          <div class="modal-title-group">
            <img
              src="/litterae_pena_fundo_transparente.png"
              alt="Logo Litterae"
              class="modal-logo"
            />
            <h3 class="modal-title">Monte sua escola</h3>
          </div>
        </div>

        <div class="modal-body">
          <div class="stepper-container">
            <div class="step active">1. Papéis</div>
            <div class="step-divider"></div>
            <div class="step">2. Pessoas</div>
          </div>

          <div class="step-content">
            <h2 class="step-title">Quais papéis sua escola usa?</h2>
            <p class="step-subtitle">
              Professor, Aluno, Administrador e Coordenador já vêm com
              permissões prontas, dá para ajustar cada uma depois.
              Administrador e Coordenador já podem cadastrar e gerenciar
              outras pessoas por você, sem nenhuma etapa extra.
            </p>

            <div class="role-grid">
              <div
                v-for="profile in SUGGESTED_MEMBER_PROFILES"
                :key="profile.relationshipType"
                class="role-card"
              >
                <div class="role-card-title">
                  {{ profile.relationshipType }}
                  <span
                    v-if="profile.permissions.canManageCircle"
                    class="role-card-badge"
                    >pode gerenciar pessoas</span
                  >
                </div>
                <div class="role-card-desc">{{ profile.description }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-primary" @click="goToAviso">
            Avançar
          </button>
        </div>
      </div>
    </div>

    <!-- Aviso: mini-modal de transição antes de abrir o cadastro de pessoas -->
    <div v-else-if="step === 'aviso'" class="success-panel">
      <div class="success-icon success-icon--info">
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#4338ca"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="9" cy="8" r="3" />
          <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
          <circle cx="17" cy="7" r="2.3" />
          <path d="M15.5 13.2c2.6.4 4.5 2.6 4.5 5.3" />
        </svg>
      </div>
      <h2 class="success-title">
        Sua escola pode cadastrar até {{ maxManagedAccounts }} pessoas
      </h2>
      <p v-if="planName" class="success-subtitle success-subtitle--plan">
        Sua conta está no plano <strong>{{ planName }}</strong>.
      </p>
      <p class="success-subtitle success-subtitle--justified">
        Agora vamos te direcionar para cadastrar professores, alunos e outros
        perfis. Se preferir, você também pode fazer isso depois, quando
        quiser, na tela de contas gerenciadas.
      </p>
      <div class="aviso-actions">
        <button type="button" class="btn btn-secondary" @click="skipForNow">
          Fazer depois
        </button>
        <button type="button" class="btn btn-primary" @click="goToPessoas">
          Cadastrar agora
        </button>
      </div>
    </div>

    <!-- Sucesso -->
    <div v-else-if="step === 'sucesso'" class="success-panel">
      <div class="success-icon">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#166534"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M5 12.5 10 17l9-10" />
        </svg>
      </div>
      <h2 class="success-title">
        {{ createdCount }}
        {{ createdCount === 1 ? "pessoa cadastrada" : "pessoas cadastradas" }}
      </h2>
      <p class="success-subtitle">
        Restam {{ Math.max(0, maxManagedAccounts - activeManagedAccounts) }} de
        {{ maxManagedAccounts }} contas do plano.
      </p>
      <button type="button" class="btn btn-success" @click="finish">
        Ir para o painel de contas
      </button>
    </div>
  </div>
</template>

<style scoped>
.modal-panel {
  max-height: calc(100vh - 4rem);
}
.form-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #fefbf5;
}

.modal-title-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}
.modal-logo {
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
  flex-shrink: 0;
}

.modal-body {
  flex-grow: 1;
}

.stepper-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding: 0 1rem;
}
.step {
  font-size: 0.875rem;
  color: var(--text-light);
  transition: color 0.3s;
}
.step.active {
  color: var(--text-dark);
  font-weight: 600;
}
.step-divider {
  flex-grow: 1;
  height: 1px;
  background-color: var(--border-color);
  margin: 0 1rem;
}
.step-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 480px;
}
.step-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-dark);
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 1rem;
}
.step-subtitle {
  font-size: 0.85rem;
  color: var(--text-light);
  margin: 0;
}
.role-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}
.role-card {
  border: 1.5px solid var(--border-color);
  border-radius: 12px;
  padding: 0.85rem 1rem;
}
.role-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.92rem;
  color: var(--text-dark);
  margin-bottom: 0.3rem;
}
.role-card-badge {
  background-color: #d8d0ff;
  color: var(--text-on-brand);
  font-size: 0.65rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
}
.role-card-desc {
  font-size: 0.8rem;
  color: var(--text-light);
}

.aviso-actions {
  display: flex;
  gap: 0.6rem;
  width: 100%;
}
.aviso-actions .btn {
  flex: 1;
}
.success-panel {
  width: 100%;
  max-width: 24rem;
  background-color: #fefbf5;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(199, 88, 88, 0.1);
  padding: 2.25rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  font-family: "Poppins", sans-serif;
}
.success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--success-bg);
  display: flex;
  align-items: center;
  justify-content: center;
}
.success-icon--info {
  background-color: var(--brand-tint);
}
.success-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}
.success-subtitle {
  font-size: 0.85rem;
  color: var(--text-light);
  margin: 0;
}
.success-subtitle--plan {
  text-align: center;
}
.success-subtitle--justified {
  text-align: justify;
}
</style>
